/**
 * How long a chat turn took, for the superadmin timer in `ChatMessages.vue`.
 *
 * Derived from the turn's own tool-call stamps — earliest `startedAt` → latest `completedAt` — which
 * the server has always persisted on `tool_calls` and returns with the conversation. Nothing extra is
 * stored or sent for this.
 *
 * It measures the TOOL WINDOW, not the whole turn: the model time before the first tool and after the
 * last one is outside it, and a turn that called no tools has nothing to measure. On a research turn
 * the tools dominate, so it is the honest bulk of the wait.
 */

/** Duration in ms, or null when the turn used no tools (or none of them finished). */
export function generationDurationMs(
  msg: { toolCalls?: { startedAt?: string; completedAt?: string | null }[] },
): number | null {
  // Both ends folded into one list: the extremes are what case, and taking min/max over the whole
  // set is agnostic to tool ordering (calls can overlap, and a resumed turn appends later ones).
  const times = (msg.toolCalls ?? [])
    .flatMap(call => [call.startedAt, call.completedAt])
    .map(stamp => (stamp ? Date.parse(stamp) : NaN))
    .filter(ms => !Number.isNaN(ms))
  if (times.length < 2) return null
  const ms = Math.max(...times) - Math.min(...times)
  return ms > 0 ? ms : null
}
